import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type { Person, Persona } from './types';
import { at, pad } from './support';

/**
 * People and personas.
 *
 * The persona is where entitlement lives. `hasFinanceEntitlement` in particular
 * is not decoration: it is what makes the masking demo real — a Domain
 * Architect calling the same endpoint as the CFO gets `annualRunCost: null` and
 * a cost KPI marked `Unavailable`, never a zero, and the field name is named
 * back to them in `meta.scope.maskedFields`.
 */

export const PERSONAS: readonly Persona[] = [
  {
    code: 'PER-01',
    name: 'Chief Information Officer',
    roleTitle: 'Chief Information Officer',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: true,
    canApprove: true,
    canEdit: true,
    canAdminister: true,
    canExport: true,
  },
  {
    code: 'PER-02',
    name: 'Chief Digital Officer',
    roleTitle: 'Chief Digital & Innovation Officer',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: true,
    canApprove: true,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-03',
    name: 'Chief Financial Officer',
    roleTitle: 'Chief Financial Officer',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: true,
    canApprove: true,
    canEdit: false,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-04',
    name: 'Chief Operating Officer',
    roleTitle: 'Chief Operating Officer',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: true,
    canApprove: true,
    canEdit: false,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-05',
    name: 'Department Leader',
    roleTitle: 'Vice President',
    scopeType: 'Department',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: true,
    canApprove: true,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-06',
    name: 'Value Stream Owner',
    roleTitle: 'Senior Director, Value Stream',
    scopeType: 'ValueStream',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: true,
    canApprove: true,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-07',
    name: 'Product Group Lead',
    roleTitle: 'Director, Product Group',
    scopeType: 'ProductGroup',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: false,
    canApprove: true,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-08',
    name: 'Product Owner',
    roleTitle: 'Product Owner',
    scopeType: 'Product',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-09',
    name: 'Product Manager',
    roleTitle: 'Product Manager',
    scopeType: 'Product',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-10',
    name: 'Enterprise Architect',
    roleTitle: 'Enterprise Architect',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-11',
    name: 'Domain Architect',
    roleTitle: 'Domain Architect',
    scopeType: 'Department',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: false,
  },
  {
    code: 'PER-12',
    name: 'Application Owner',
    roleTitle: 'Application Owner',
    scopeType: 'Product',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: false,
  },
  {
    code: 'PER-13',
    name: 'Delivery Lead',
    roleTitle: 'Delivery Lead',
    scopeType: 'ProductGroup',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: true,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-14',
    name: 'Engineering Manager',
    roleTitle: 'Engineering Manager',
    scopeType: 'Product',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: true,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: false,
  },
  {
    code: 'PER-15',
    name: 'Portfolio Analyst',
    roleTitle: 'Portfolio Analyst',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: false,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-16',
    name: 'Finance Business Partner',
    roleTitle: 'Finance Business Partner',
    scopeType: 'Department',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: false,
    canApprove: true,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-17',
    name: 'Data Steward',
    roleTitle: 'Data Steward',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: false,
    canApprove: true,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-18',
    name: 'Data Governance Lead',
    roleTitle: 'Head of Data Governance',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: false,
    canApprove: true,
    canEdit: true,
    canAdminister: true,
    canExport: true,
  },
  {
    code: 'PER-19',
    name: 'Platform Operations Lead',
    roleTitle: 'Platform Operations Lead',
    scopeType: 'ValueStream',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: false,
  },
  {
    code: 'PER-20',
    name: 'Sourcing Manager',
    roleTitle: 'IT Sourcing Manager',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-21',
    name: 'Contract Manager',
    roleTitle: 'Contract Manager',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: false,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-22',
    name: 'Risk & Compliance Officer',
    roleTitle: 'Risk & Compliance Officer',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: false,
    hasWorkforceEntitlement: false,
    canApprove: true,
    canEdit: false,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-23',
    name: 'Workforce Planning Lead',
    roleTitle: 'Workforce Planning Lead',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: true,
    canApprove: false,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
  {
    code: 'PER-24',
    name: 'Transformation Lead',
    roleTitle: 'Head of Transformation',
    scopeType: 'Enterprise',
    hasFinanceEntitlement: true,
    hasWorkforceEntitlement: true,
    canApprove: true,
    canEdit: true,
    canAdminister: false,
    canExport: true,
  },
];

interface PersonSeed {
  code: string;
  displayName: string;
  jobTitle: string;
  departmentCode: string;
  personaCode: string;
  scopeKind: Person['scopeKind'];
  scopeCode: string;
}

/**
 * Sixty named people. Curated rather than generated because names appear in
 * screenshots and a portfolio owned by "Person 0041" reads as a prototype.
 */
const PERSON_SEEDS: readonly PersonSeed[] = [
  // Enterprise leadership
  {
    code: 'PPL-0001',
    displayName: 'Ingrid Halvorsen',
    jobTitle: 'Chief Information Officer',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-01',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0002',
    displayName: 'Marcus Oyelaran',
    jobTitle: 'Chief Digital & Innovation Officer',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-02',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0003',
    displayName: 'Diane Whitcombe',
    jobTitle: 'Chief Financial Officer',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-03',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0004',
    displayName: 'Rafael Cardoso',
    jobTitle: 'Chief Operating Officer',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-04',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0005',
    displayName: 'Priya Raghunathan',
    jobTitle: 'Enterprise Architect',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-10',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0006',
    displayName: 'Tom Eriksen',
    jobTitle: 'Head of Transformation',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-24',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0007',
    displayName: 'Nadia Benali',
    jobTitle: 'Head of Data Governance',
    departmentCode: 'DEP-DATA',
    personaCode: 'PER-18',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0008',
    displayName: 'Colin Traoré',
    jobTitle: 'Portfolio Analyst',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-15',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0009',
    displayName: 'Beatriz Lund',
    jobTitle: 'Risk & Compliance Officer',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-22',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0010',
    displayName: 'Samir Qureshi',
    jobTitle: 'IT Sourcing Manager',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-20',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0011',
    displayName: 'Grace Okonkwo',
    jobTitle: 'Contract Manager',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-21',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0012',
    displayName: 'Helena Vasquez',
    jobTitle: 'Workforce Planning Lead',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-23',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0013',
    displayName: 'Anders Feldt',
    jobTitle: 'Data Steward, Applications',
    departmentCode: 'DEP-DATA',
    personaCode: 'PER-17',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },
  {
    code: 'PPL-0014',
    displayName: 'Meera Sundaram',
    jobTitle: 'Data Steward, Finance',
    departmentCode: 'DEP-DATA',
    personaCode: 'PER-17',
    scopeKind: 'Enterprise',
    scopeCode: 'ENT-AURORA',
  },

  // Department leaders
  {
    code: 'PPL-0015',
    displayName: 'Julia Sandberg',
    jobTitle: 'VP, Network & Operations Technology',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-05',
    scopeKind: 'Department',
    scopeCode: 'DEP-NOPS',
  },
  {
    code: 'PPL-0016',
    displayName: 'Owen Mbeki',
    jobTitle: 'VP, Customer Experience Technology',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-05',
    scopeKind: 'Department',
    scopeCode: 'DEP-CX',
  },
  {
    code: 'PPL-0017',
    displayName: 'Sofia Marchetti',
    jobTitle: 'VP, Commercial Technology',
    departmentCode: 'DEP-COMM',
    personaCode: 'PER-05',
    scopeKind: 'Department',
    scopeCode: 'DEP-COMM',
  },
  {
    code: 'PPL-0018',
    displayName: 'Nathan Boateng',
    jobTitle: 'VP, Corporate & Shared Services',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-05',
    scopeKind: 'Department',
    scopeCode: 'DEP-CORP',
  },
  {
    code: 'PPL-0019',
    displayName: 'Yuki Nakamura',
    jobTitle: 'VP, Data, Analytics & AI',
    departmentCode: 'DEP-DATA',
    personaCode: 'PER-05',
    scopeKind: 'Department',
    scopeCode: 'DEP-DATA',
  },

  // Value stream owners
  {
    code: 'PPL-0020',
    displayName: 'Maria Alvarez',
    jobTitle: 'Senior Director, Crew Operations',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-CREW',
  },
  {
    code: 'PPL-0021',
    displayName: 'Daniel Okafor',
    jobTitle: 'Senior Director, Flight Operations',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-FLTOPS',
  },
  {
    code: 'PPL-0022',
    displayName: 'Ruth Callaghan',
    jobTitle: 'Senior Director, Airport Journey',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-ARPT',
  },
  {
    code: 'PPL-0023',
    displayName: 'Ivan Petrov',
    jobTitle: 'Senior Director, Maintenance & Engineering',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-MTCE',
  },
  {
    code: 'PPL-0024',
    displayName: 'Amelia Ferreira',
    jobTitle: 'Senior Director, Network Planning',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-NETPLAN',
  },
  {
    code: 'PPL-0025',
    displayName: 'Jonas Lindqvist',
    jobTitle: 'Senior Director, Book & Manage Trip',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-BOOK',
  },
  {
    code: 'PPL-0026',
    displayName: 'Chantelle Duval',
    jobTitle: 'Senior Director, Customer Care',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-CARE',
  },
  {
    code: 'PPL-0027',
    displayName: 'Victor Nyman',
    jobTitle: 'Senior Director, Loyalty & Partnerships',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-LOYALTY',
  },
  {
    code: 'PPL-0028',
    displayName: 'Aisha Rahman',
    jobTitle: 'Senior Director, Pricing & Revenue Management',
    departmentCode: 'DEP-COMM',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-PRM',
  },
  {
    code: 'PPL-0029',
    displayName: 'Peter Lindgren',
    jobTitle: 'Senior Director, Ancillary & Retailing',
    departmentCode: 'DEP-COMM',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-RETAIL',
  },
  {
    code: 'PPL-0030',
    displayName: 'Nora Sadiq',
    jobTitle: 'Senior Director, Cargo',
    departmentCode: 'DEP-COMM',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-CARGO',
  },
  {
    code: 'PPL-0031',
    displayName: 'Gareth Prydderch',
    jobTitle: 'Senior Director, Finance Systems',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-FIN',
  },
  {
    code: 'PPL-0032',
    displayName: 'Lucia Marchand',
    jobTitle: 'Senior Director, People & Workplace',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-PEOPLE',
  },
  {
    code: 'PPL-0033',
    displayName: 'Kenji Watanabe',
    jobTitle: 'Senior Director, Data & AI Platform',
    departmentCode: 'DEP-DATA',
    personaCode: 'PER-06',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-DATAAI',
  },

  // Product group leads
  {
    code: 'PPL-0034',
    displayName: 'Elena Rojas',
    jobTitle: 'Director, Crew Planning',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-CREWPLAN',
  },
  {
    code: 'PPL-0035',
    displayName: 'Hugh Bannerman',
    jobTitle: 'Director, Crew Day-of-Ops',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-CREWOPS',
  },
  {
    code: 'PPL-0036',
    displayName: 'Farida Osman',
    jobTitle: 'Director, Airport Turn',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-TURN',
  },
  {
    code: 'PPL-0037',
    displayName: 'Miguel Santos',
    jobTitle: 'Director, Baggage',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-BAG',
  },
  {
    code: 'PPL-0038',
    displayName: 'Clare Devereux',
    jobTitle: 'Director, Passenger Processing',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-PAX',
  },
  {
    code: 'PPL-0039',
    displayName: 'Stefan Brandt',
    jobTitle: 'Director, Flight Dispatch & Planning',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-DISPATCH',
  },
  {
    code: 'PPL-0040',
    displayName: 'Ingrid Solberg',
    jobTitle: 'Director, Fuel & Efficiency',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-FUEL',
  },
  {
    code: 'PPL-0041',
    displayName: 'Ade Fashola',
    jobTitle: 'Director, Shopping & Booking',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-SHOP',
  },
  {
    code: 'PPL-0042',
    displayName: 'Karin Møller',
    jobTitle: 'Director, Trip Servicing',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-TRIP',
  },
  {
    code: 'PPL-0043',
    displayName: 'Tobias Reinhardt',
    jobTitle: 'Director, Loyalty Core',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-LOYCORE',
  },
  {
    code: 'PPL-0044',
    displayName: 'Bernadette Chu',
    jobTitle: 'Director, Revenue Accounting',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-REVACC',
  },
  {
    code: 'PPL-0045',
    displayName: 'Luca Bergamini',
    jobTitle: 'Director, Payments',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-07',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-PAY',
  },

  // Product owners, managers, architects, engineering
  {
    code: 'PPL-0046',
    displayName: 'Simone Adeyemi',
    jobTitle: 'Product Owner, Crew Disruption Recovery',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-08',
    scopeKind: 'Product',
    scopeCode: 'PRD-CDR',
  },
  {
    code: 'PPL-0047',
    displayName: 'Henrik Vasilev',
    jobTitle: 'Product Manager, Crew Disruption Recovery',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-09',
    scopeKind: 'Product',
    scopeCode: 'PRD-CDR',
  },
  {
    code: 'PPL-0048',
    displayName: 'Tessa Moreau',
    jobTitle: 'Product Owner, Flight Booking',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-08',
    scopeKind: 'Product',
    scopeCode: 'PRD-BOOK',
  },
  {
    code: 'PPL-0049',
    displayName: 'Kwame Asante',
    jobTitle: 'Product Owner, Baggage Journey',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-08',
    scopeKind: 'Product',
    scopeCode: 'PRD-BAGJ',
  },
  {
    code: 'PPL-0050',
    displayName: 'Renata Kovalenko',
    jobTitle: 'Product Owner, Check-in & Boarding',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-08',
    scopeKind: 'Product',
    scopeCode: 'PRD-CHKB',
  },
  {
    code: 'PPL-0051',
    displayName: 'Dominic Ashworth',
    jobTitle: 'Product Owner, Revenue Accounting',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-08',
    scopeKind: 'Product',
    scopeCode: 'PRD-REVA',
  },
  {
    code: 'PPL-0052',
    displayName: 'Aya Tanaka',
    jobTitle: 'Product Owner, Customer 360',
    departmentCode: 'DEP-DATA',
    personaCode: 'PER-08',
    scopeKind: 'Product',
    scopeCode: 'PRD-C360',
  },
  {
    code: 'PPL-0053',
    displayName: 'Louis Ferrand',
    jobTitle: 'Domain Architect, Flight Operations',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-11',
    scopeKind: 'Department',
    scopeCode: 'DEP-NOPS',
  },
  {
    code: 'PPL-0054',
    displayName: 'Fiona Mackenzie',
    jobTitle: 'Domain Architect, Customer Experience',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-11',
    scopeKind: 'Department',
    scopeCode: 'DEP-CX',
  },
  {
    code: 'PPL-0055',
    displayName: 'Arjun Malhotra',
    jobTitle: 'Delivery Lead, Crew Day-of-Ops',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-13',
    scopeKind: 'ProductGroup',
    scopeCode: 'PG-CREWOPS',
  },
  {
    code: 'PPL-0056',
    displayName: 'Sinead O’Rourke',
    jobTitle: 'Engineering Manager, Crew Platform',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-14',
    scopeKind: 'Product',
    scopeCode: 'PRD-CDR',
  },
  {
    code: 'PPL-0057',
    displayName: 'Paulo Nogueira',
    jobTitle: 'Platform Operations Lead, Airport Journey',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-19',
    scopeKind: 'ValueStream',
    scopeCode: 'VS-ARPT',
  },
  {
    code: 'PPL-0058',
    displayName: 'Anneke de Vries',
    jobTitle: 'Finance Business Partner, Network & Operations',
    departmentCode: 'DEP-CORP',
    personaCode: 'PER-16',
    scopeKind: 'Department',
    scopeCode: 'DEP-NOPS',
  },
  {
    code: 'PPL-0059',
    displayName: 'Mateo Guzmán',
    jobTitle: 'Application Owner, Reservations Platform',
    departmentCode: 'DEP-CX',
    personaCode: 'PER-12',
    scopeKind: 'Product',
    scopeCode: 'PRD-BOOK',
  },
  {
    code: 'PPL-0060',
    displayName: 'Zara Haddad',
    jobTitle: 'Application Owner, Crew Systems',
    departmentCode: 'DEP-NOPS',
    personaCode: 'PER-12',
    scopeKind: 'Product',
    scopeCode: 'PRD-CDR',
  },
];

function emailFor(displayName: string, index: number): string {
  const parts = displayName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z ]/g, '')
    .toLowerCase()
    .split(' ')
    .filter(Boolean);
  const first = at(parts, 0);
  const last = parts.length > 1 ? at(parts, parts.length - 1) : `p${pad(index, 4)}`;
  return `${first}.${last}@auroraairlines.example`;
}

export function buildPeople(asOf: string, rng: Rng): { personas: Persona[]; people: Person[] } {
  const stamp = provenanceFactory(asOf, rng.fork('people'));

  const people = PERSON_SEEDS.map((seed, index) => ({
    id: idFor('Person', seed.code),
    businessCode: seed.code,
    name: seed.displayName,
    displayName: seed.displayName,
    jobTitle: seed.jobTitle,
    email: emailFor(seed.displayName, index + 1),
    departmentCode: seed.departmentCode,
    personaCode: seed.personaCode,
    scopeKind: seed.scopeKind,
    scopeCode: seed.scopeCode,
    provenance: stamp(SOURCE_SYSTEMS.hcm, `HCM-${pad(index + 1, 6)}`, {
      meanAgeDays: 9,
      meanConfidence: 0.93,
      dataClassification: 'Confidential',
    }),
  }));

  return { personas: PERSONAS.slice(), people };
}
